import React, { useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import useInvisibleRecaptcha from '../hooks/useInvisibleRecaptcha';
import { useSettings } from '../context/SettingsContext';
import { CheckIcon } from '../components/icons';
import './QuotePage.css';
import { apiFetch } from '../services/api';
import { trackQuoteRequest } from '../services/googleAds';
import { RECAPTCHA_SITE_KEY } from '../config/env';
import { WARRANTY_YEARS } from '../config/warranty';
import { AWNING_FROM_LABEL } from '../config/startingPrice';
import { STOCK_SHORT, LEAD_PERGOLA } from '../config/leadTimes';

function QuotePage() {
  // CQ-22: see ContactPage for the SettingsContext rationale.
  const settings = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // UX-03: the form was leading with an internal-jargon "Ceiling Type"
  // dropdown that meant nothing to homeowners. Now mirrors the homepage
  // quote form's stepped flow — pick a product first, see relevant
  // fields second. '' | 'awning' | 'pergola'.
  // /colours links here as /request-quote?colour=AW-SC-2170. A colour only
  // applies to awnings, so arriving with one also skips the category step.
  const [searchParams] = useSearchParams();
  const presetColour = (searchParams.get('colour') || '').trim().slice(0, 40);
  const [quoteCategory, setQuoteCategory] = useState(presetColour ? 'awning' : '');
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState('');
  const formRef = useRef(null);

  const s = (key, fallback = '') => settings[key] !== undefined ? settings[key] : fallback;

  // Client-side guard rails for the photo upload. The server enforces the
  // same limits (middleware/upload.js), this just gives instant feedback.
  const MAX_PHOTOS = 5;
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  const PHOTO_EXT_RE = /\.(jpe?g|png|webp|heic|heif)$/i;
  const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotoError('');
    if (files.length > MAX_PHOTOS) {
      setPhotoError(`Please select up to ${MAX_PHOTOS} photos.`);
      e.target.value = '';
      setPhotos([]);
      return;
    }
    for (const f of files) {
      // HEIC often arrives with an empty MIME type, so fall back to the extension.
      const typeOk = PHOTO_MIMES.includes(f.type) || PHOTO_EXT_RE.test(f.name);
      if (!typeOk) {
        setPhotoError('Photos must be JPG, PNG, WEBP or HEIC.');
        e.target.value = '';
        setPhotos([]);
        return;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        setPhotoError(`Each photo must be under 10MB - "${f.name}" is too large.`);
        e.target.value = '';
        setPhotos([]);
        return;
      }
    }
    setPhotos(files);
  };

  const submitForm = useCallback(async (token) => {
    const form = formRef.current;
    if (!form) return;
    // Send multipart form-data so the customer's photos ride along. Normalise
    // the attachments[] checkbox name to a plain repeated `attachments` field;
    // the file input (name="photos") is picked up by FormData automatically.
    const raw = new FormData(form);
    const out = new FormData();
    raw.forEach((v, k) => {
      if (k === 'attachments[]') out.append('attachments', v);
      else out.append(k, v);
    });
    if (token) out.append('recaptchaToken', token);

    try {
      const res = await apiFetch('/api/quotes', {
        method: 'POST',
        body: out,
      });
      if (res.ok) {
        // Counted only once the server has accepted the request.
        trackQuoteRequest({ email: raw.get('email'), phone: raw.get('phone') });
        setSubmitted(true);
        form.reset();
        setPhotos([]);
      } else {
        const err = await res.json().catch(() => ({}));
        setSubmitError(err.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
    recaptchaReset();
    setSubmitting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reCAPTCHA can fail in ways that never call onVerify - the visitor dismisses
  // the challenge, it expires, or the script is blocked. Without this the
  // button sits on "Submitting..." forever and the lead is lost silently.
  const handleRecaptchaUnavailable = useCallback((reason) => {
    setSubmitting(false);
    setSubmitError(
      reason === 'blocked'
        ? "We couldn't load the spam check - an ad blocker or privacy extension is usually the cause. Please allow it for this page and try again, or email info@asgshading.com.au or call +61 422 439 499 and we'll take your details."
        : reason === 'expired'
          ? "The spam check timed out. Please try again."
          : "The spam check could not be completed. Please try again, or email info@asgshading.com.au or call +61 422 439 499."
    );
  }, []);


  const { containerRef: recaptchaRef, execute: recaptchaExecute, reset: recaptchaReset, prepare: loadRecaptcha } =
    useInvisibleRecaptcha({ siteKey: RECAPTCHA_SITE_KEY, onVerify: submitForm, onUnavailable: handleRecaptchaUnavailable });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    recaptchaExecute();
  };

  return (
    <div className="quote-page">
      <SEO
        title="Request a Quote - Awnings &amp; Pergolas Melbourne"
        description="Get a quote for a retractable awning (free delivery to most of Melbourne) or a supply-only pergola system. Detailed written quote, no obligation."
        keywords="awning quote Melbourne, retractable awning quote, pergola supply quote Melbourne, pergola cost Melbourne, outdoor shading quote"
        canonical="/request-quote"
        jsonLd={[SEO.localBusinessSchema, SEO.breadcrumbSchema([{ name: 'Request Quote' }]), SEO.webPageSchema({ type: 'WebPage', name: 'Request a No-Obligation Quote', description: 'Get a no-obligation quote for an awning or pergola supply in Melbourne.', url: '/request-quote' })]}
      />
      <section className="qp-hero">
        <div className="qp-hero-bg"></div>
        <div className="qp-hero-content">
          <p className="qp-label">Get Started</p>
          <h1 className="qp-title">Request Quote Now</h1>
          <p className="qp-subtitle">Send us a few photos and your measurements and we'll advise remotely - honest advice and a detailed written quote, no site visit needed.</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Request Quote' }]} />

      <section className="qp-main">
        <div className="qp-main-inner">
          <div className="qp-info">
            <div className="qp-info-card">
              <CheckIcon />
              <h2>Awnings: Made to Measure</h2>
              <p>Our retractable awning range is built to your dimensions and delivered free across the Melbourne metro area.</p>
            </div>
            <div className="qp-info-card">
              <CheckIcon />
              <h2>Pergolas: Supply Only</h2>
              <p>Pergolas are built to your spec and supplied to your address. Installation and permits are arranged by your licensed builder.</p>
            </div>
            <div className="qp-info-card">
              <CheckIcon />
              <h2>Delivery Included</h2>
              <p>Free delivery to local Melbournians on awnings; pergola delivery quoted with each order.</p>
            </div>
            <div className="qp-info-card">
              <CheckIcon />
              <h2>{WARRANTY_YEARS}-Year Warranty</h2>
              <p>Every product is backed by a {WARRANTY_YEARS}-year warranty.*</p>
            </div>
            <div className="qp-info-card qp-info-contact">
              <h2>Prefer to Talk?</h2>
              <a href="tel:+61422439499">+61 422 439 499</a>
              <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a>
            </div>
          </div>

          <div className="qp-form-wrap">
            {submitted ? (
              <div className="qp-success">
                <svg width="48" height="48" fill="none" style={{ stroke: 'var(--asg-accent)' }} strokeWidth="1.5" viewBox="0 0 24 24">
                  <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <h2>Quote Request Sent</h2>
                <p>Thank you! We'll review your details and get back to you within 24 hours.</p>
                <button className="qp-reset-btn" onClick={() => setSubmitted(false)}>Submit Another Quote</button>
              </div>
            ) : (
              <form className="qp-form" ref={formRef} onSubmit={handleSubmit} onFocus={loadRecaptcha}>
                {/* STEP 1 — what kind of product? */}
                {!quoteCategory && (
                  <div className="qp-form-step">
                    <p className="qp-step-label">What are you after?</p>
                    <div className="qp-cat-grid">
                      <button type="button" className="qp-cat-card" onClick={() => setQuoteCategory('awning')}>
                        <span className="qp-cat-card-title">Retractable Awning</span>
                        <span className="qp-cat-card-sub">{AWNING_FROM_LABEL} · Free delivery to most of Melbourne · {STOCK_SHORT}</span>
                      </button>
                      <button type="button" className="qp-cat-card" onClick={() => setQuoteCategory('pergola')}>
                        <span className="qp-cat-card-title">Aluminium Pergola</span>
                        <span className="qp-cat-card-sub">Supply only · {LEAD_PERGOLA} · You arrange installer</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 2 — category-specific + contact fields */}
                {quoteCategory && (
                  <>
                    <div className="qp-cat-pill">
                      <span>For: <strong>{quoteCategory === 'awning' ? 'Awning' : 'Pergola'}</strong></span>
                      <button type="button" className="qp-cat-change" onClick={() => setQuoteCategory('')}>Change</button>
                    </div>
                    <input type="hidden" name="category" value={quoteCategory} />

                    {/* AWNING-specific */}
                    {quoteCategory === 'awning' && (
                      <>
                        <div className="qp-form-row">
                          <div className="qp-field">
                            <label>Which model?</label>
                            <select name="model" defaultValue="">
                              <option value="" disabled>Select</option>
                              <option value="AW01 Full Cassette Awning">AW01 Full Cassette Awning</option>
                              <option value="B05 Traditional Scalloped">B05 Traditional Scalloped</option>
                              <option value="B06 Premium Open-Arm">B06 Premium Open-Arm</option>
                              <option value="A09 Long-Projection">A09 Long-Projection</option>
                              <option value="K3 Heritage Mediterranean">K3 Heritage Mediterranean</option>
                              <option value="Not sure yet">Not sure yet</option>
                            </select>
                          </div>
                          <div className="qp-field">
                            <label>Approx. dimensions (W × P)</label>
                            <input type="text" name="total_size" placeholder="e.g. 4m × 3m" />
                          </div>
                        </div>
                        <div className="qp-field">
                          <label>Fabric colour</label>
                          <input
                            type="text"
                            name="fabric_colour"
                            defaultValue={presetColour}
                            placeholder="e.g. AW-SC-2170 Charcoal Grey, or leave blank"
                          />
                          {presetColour && (
                            <p className="qp-field-note">Pre-filled from the colour you picked.</p>
                          )}
                        </div>
                        <div className="qp-field">
                          <label>Mounting</label>
                          <div className="qp-checkbox-row">
                            <label className="qp-checkbox-label"><input type="radio" name="ceiling_type" value="Wall mount" /> Wall mount</label>
                            <label className="qp-checkbox-label"><input type="radio" name="ceiling_type" value="Ceiling/soffit mount" /> Ceiling/soffit</label>
                            <label className="qp-checkbox-label"><input type="radio" name="ceiling_type" value="Not sure" /> Not sure</label>
                          </div>
                        </div>
                        <div className="qp-field">
                          <label>Optional extras</label>
                          <div className="qp-checkbox-row">
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="LED Lighting" /> LED Lighting</label>
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="Wind sensor" /> Wind sensor</label>
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="Motorised" /> Motorised</label>
                          </div>
                        </div>
                      </>
                    )}

                    {/* PERGOLA-specific */}
                    {quoteCategory === 'pergola' && (
                      <>
                        <div className="qp-form-row">
                          <div className="qp-field">
                            <label>Which model?</label>
                            <select name="model" defaultValue="">
                              <option value="" disabled>Select</option>
                              <option value="Pergola 163">Pergola 163</option>
                              <option value="Pergola 175">Pergola 175</option>
                              <option value="Pergola 180 Retractable">Pergola 180 Retractable</option>
                              <option value="Not sure yet">Not sure yet</option>
                            </select>
                          </div>
                          <div className="qp-field">
                            <label>Approx. dimensions (L × W)</label>
                            <input type="text" name="total_size" placeholder="e.g. 6m × 4m" />
                          </div>
                        </div>
                        <div className="qp-form-row">
                          <div className="qp-field">
                            <label>Configuration</label>
                            <select name="stands" defaultValue="">
                              <option value="" disabled>Select</option>
                              <option value="Freestanding - 4 posts">Freestanding (4 posts)</option>
                              <option value="Wall-mounted - 2 posts">Wall-mounted (2 posts)</option>
                              <option value="Not sure yet">Not sure yet</option>
                            </select>
                          </div>
                        </div>
                        <div className="qp-field">
                          <label>Optional extras</label>
                          <div className="qp-checkbox-row">
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="LED Lighting" /> LED Lighting</label>
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="Glass walls" /> Glass walls</label>
                            <label className="qp-checkbox-label"><input type="checkbox" name="attachments[]" value="Heaters" /> Heaters</label>
                          </div>
                        </div>
                      </>
                    )}

                    {/* COMMON: photos + contact fields + message */}
                    <div className="qp-field">
                      <label>Photos of your space (optional)</label>
                      <p className="qp-help">
                        We're supply-only and quote remotely - a few photos let us confirm the
                        largest model that fits. Helpful shots: the full wall from a few metres
                        back, a close-up of the wall surface, and anything in the way such as
                        downpipes, windows or meter boxes. Up to 5 images (JPG, PNG, WEBP or HEIC),
                        10MB each.
                      </p>
                      <input
                        type="file"
                        name="photos"
                        className="qp-file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                        multiple
                        onChange={handlePhotoChange}
                      />
                      {photoError && <p className="asg-form-error" role="alert">{photoError}</p>}
                      {photos.length > 0 && (
                        <ul className="qp-photo-list">
                          {photos.map((f, i) => <li key={i}>{f.name}</li>)}
                        </ul>
                      )}
                    </div>

                    <div className="qp-form-divider"><span>Your details</span></div>
                    <div className="qp-form-row">
                      <div className="qp-field">
                        <label>Full Name *</label>
                        <input type="text" name="full_name" required placeholder="Your name" autoComplete="name" />
                      </div>
                      <div className="qp-field">
                        <label>Email *</label>
                        <input type="email" name="email" required placeholder="your@email.com" autoComplete="email" />
                      </div>
                    </div>
                    <div className="qp-form-row">
                      <div className="qp-field">
                        <label>Phone</label>
                        <input type="tel" name="phone" placeholder="04XX XXX XXX" autoComplete="tel" />
                      </div>
                      <div className="qp-field">
                        <label>Suburb</label>
                        <input type="text" name="suburb" placeholder="e.g. Richmond" />
                      </div>
                    </div>
                    <div className="qp-field">
                      <label>Message</label>
                      <textarea name="message" rows="4" placeholder="Tell us about your space, style preferences, or any questions..."></textarea>
                    </div>
                    {RECAPTCHA_SITE_KEY && <div ref={recaptchaRef}></div>}
                    {submitError && <p className="asg-form-error" role="alert">{submitError}</p>}
                    <button type="submit" className="qp-submit" disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Request Quote'}
                    </button>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default QuotePage;
